/**
 * Formats an error into a readable string message for BTHome flows.
 *
 * @param {unknown} error - Error object, string, or unknown type
 * @returns {string} Formatted error message
 */
export const formatBTHomeErrorMessage = (error: unknown): string => {
	if (error instanceof Error) {
		return error.message || 'Unknown error';
	}

	if (typeof error === 'string') {
		return error || 'Unknown error';
	}

	if (typeof error === 'object' && error !== null) {
		if ('message' in error && typeof (error as { message: unknown }).message === 'string') {
			return ((error as { message: string }).message as string) || 'Unknown error';
		}

		try {
			return JSON.stringify(error);
		} catch {
			return 'Unknown error';
		}
	}

	return String(error || 'Unknown error');
};

/**
 * Builds parameters for the BTHome startLearning RPC call from a generic input object.
 *
 * @param {any} input - Input object, typically containing `id` or `name`
 * @returns {Record<string, any>} Params object for the RPC call
 */
export const buildBTHomeLearningParams = (input: any): Record<string, any> => {
	if (input && typeof input.name === 'string' && input.name) {
		return { component_key: input.name };
	}
	if (input?.id !== undefined && !Number.isNaN(Number(input.id))) {
		return { input_id: Number(input.id) };
	}
	return {};
};

/**
 * Detects if a BTHome RPC error represents a "Learn in progress" condition.
 *
 * @param {unknown} rawError - Raw error payload from the RPC response
 * @param {any} deviceError - Nested device_error object if available
 * @returns {boolean} True if error indicates learning is already in progress
 */
export const isBTHomeLearnInProgressRpcError = (rawError: unknown, deviceError?: any): boolean => {
	if (deviceError?.code === -114) {
		return true;
	}

	if (typeof rawError === 'string' && rawError.includes('Learn in progress')) {
		return true;
	}

	if (rawError !== null && rawError !== undefined && typeof rawError === 'object') {
		const typedError = rawError as Record<string, any>;
		if (
			typedError.device_error &&
			typeof typedError.device_error === 'object' &&
			typedError.device_error.code === -114
		) {
			return true;
		}
		if (
			typedError.error === 'JRPC_ERROR' &&
			typedError.device_error &&
			typedError.device_error.code === -114
		) {
			return true;
		}
	}

	return false;
};

/**
 * Detects if a free‑form error message string represents a "Learn in progress" condition.
 *
 * @param {string} message - Error message string
 * @returns {boolean} True if message indicates learning is already in progress
 */
export const isBTHomeLearnInProgressMessage = (message: string): boolean => {
	if (!message) {
		return false;
	}
	return (
		message.includes('Learn in progress') ||
		message.includes('"code":-114') ||
		message.includes('code":-114') ||
		message.includes('Resource unavailable')
	);
};
